<template>
  <div class="iq-card iq-card-sm">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">
          {{ $t('pos.lbl_cart') }}
          <span class="badge bg-primary ms-1" v-if="lineCount">{{ lineCount }}</span>
        </h5>
        <button type="button" class="btn btn-sm btn-link text-danger p-0 pos-tap" v-if="store.hasLines" @click="store.clearCart()">
          <i class="fa-solid fa-trash-can me-1" aria-hidden="true"></i>{{ $t('pos.lbl_clear_cart') }}
        </button>
      </div>

      <p class="text-muted mb-0" v-if="!store.hasLines">{{ $t('pos.lbl_empty_cart') }}</p>

      <!-- Servicios: empleado + hora -->
      <div v-for="line in store.services" :key="line.uid" class="pos-line">
        <div class="d-flex justify-content-between align-items-start gap-2">
          <div>
            <strong>{{ line.name }}</strong>
            <small class="text-muted d-block">{{ line.duration_min }} {{ $t('pos.lbl_min') }}</small>
          </div>
          <div class="text-end">
            <strong>{{ money(line.price) }}</strong>
            <button
              type="button"
              class="btn btn-sm btn-link text-danger d-block ms-auto pos-tap"
              :aria-label="`${$t('pos.lbl_remove')} ${line.name}`"
              @click="store.removeService(line.uid)"
            >
              {{ $t('pos.lbl_remove') }}
            </button>
          </div>
        </div>

        <div class="row g-2 mt-1">
          <div class="col-md-6">
            <label class="form-label small mb-1" :for="`emp-${line.uid}`">{{ $t('pos.lbl_employee') }} <span class="text-danger">*</span></label>
            <select
              :id="`emp-${line.uid}`"
              class="form-select form-select-sm"
              :value="line.employee_id"
              :disabled="line.loadingEmployees"
              @change="store.setServiceEmployee(line.uid, $event.target.value)"
            >
              <option :value="null">{{ line.loadingEmployees ? $t('pos.lbl_loading') : $t('pos.lbl_select_employee') }}</option>
              <option v-for="employee in line.employees" :key="`${line.uid}-emp-${employee.id}`" :value="employee.id">
                {{ employeeName(employee) }}
              </option>
            </select>
            <!-- Solo tras consultar: si no, parpadea "sin empleados" mientras carga. -->
            <small class="text-danger" v-if="line.employeesLoaded && !line.employees.length">{{ $t('pos.lbl_no_employees') }}</small>
          </div>
          <div class="col-md-6">
            <label class="form-label small mb-1" :for="`start-${line.uid}`">{{ $t('pos.lbl_date_time') }}</label>
            <input
              :id="`start-${line.uid}`"
              type="datetime-local"
              class="form-control form-control-sm"
              :value="line.start_date_time"
              @input="store.setServiceStart(line.uid, $event.target.value)"
            />
          </div>
        </div>
      </div>

      <!-- Productos: cantidad limitada por stock -->
      <div v-for="line in store.products" :key="line.uid" class="pos-line">
        <div class="d-flex justify-content-between align-items-start gap-2">
          <div>
            <strong>{{ line.name }}</strong>
            <small class="text-muted d-block" v-if="line.variation_name">{{ line.variation_name }}</small>
            <small class="text-muted d-block">{{ money(line.unit_price) }} · {{ $t('pos.lbl_stock') }}: {{ line.stock }}</small>
          </div>
          <div class="text-end">
            <strong>{{ money(lineTotal(line)) }}</strong>
            <small class="text-success d-block" v-if="line.line_discount > 0">- {{ money(line.line_discount) }}</small>
            <button
              type="button"
              class="btn btn-sm btn-link text-danger d-block ms-auto pos-tap"
              :aria-label="`${$t('pos.lbl_remove')} ${line.name}`"
              @click="store.removeProduct(line.uid)"
            >
              {{ $t('pos.lbl_remove') }}
            </button>
          </div>
        </div>

        <div class="d-flex align-items-center gap-2 mt-1">
          <label class="form-label small mb-0" :for="`qty-${line.uid}`">{{ $t('pos.lbl_qty') }}</label>
          <div class="input-group input-group-sm pos-qty">
            <button type="button" class="btn btn-outline-secondary pos-tap" :aria-label="'-'" @click="store.setProductQty(line.uid, line.qty - 1)">-</button>
            <input
              :id="`qty-${line.uid}`"
              type="number"
              class="form-control text-center"
              min="1"
              :max="line.stock"
              :value="line.qty"
              @input="store.setProductQty(line.uid, $event.target.value)"
            />
            <button
              type="button"
              class="btn btn-outline-secondary pos-tap"
              :aria-label="'+'"
              :disabled="line.qty >= line.stock"
              @click="store.setProductQty(line.uid, line.qty + 1)"
            >
              +
            </button>
          </div>
          <small class="text-danger" v-if="line.qty >= line.stock">{{ $t('pos.lbl_stock_exceeded') }}</small>
        </div>

        <!-- Descuento MANUAL por linea (% o importe fijo). null = sin override. -->
        <div class="d-flex align-items-center gap-2 mt-1 pos-line-disc">
          <label class="form-label small mb-0">{{ $t('pos.lbl_line_discount') }}</label>
          <div class="input-group input-group-sm">
            <button type="button" class="btn" :class="line.manual_discount_type === 'percent' ? 'btn-primary' : 'btn-outline-secondary'" @click="store.setLineDiscountType(line.uid, 'percent')">%</button>
            <button type="button" class="btn" :class="line.manual_discount_type === 'fixed' ? 'btn-primary' : 'btn-outline-secondary'" @click="store.setLineDiscountType(line.uid, 'fixed')">{{ currencySymbol }}</button>
            <input
              type="number"
              min="0"
              step="0.01"
              class="form-control"
              :placeholder="line.manual_discount_type === 'percent' ? '0 - 100' : '0.00'"
              :disabled="!line.manual_discount_type"
              :value="line.manual_discount_value"
              @input="store.setLineDiscountValue(line.uid, $event.target.value)"
            />
            <button type="button" class="btn btn-outline-danger" v-if="line.manual_discount_type" @click="store.clearLineDiscount(line.uid)" :aria-label="$t('pos.lbl_clear')">
              <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="mt-3" v-if="store.hasLines">
        <label class="form-label small mb-1" for="pos-note">{{ $t('pos.lbl_note') }}</label>
        <textarea id="pos-note" class="form-control form-control-sm" rows="2" :placeholder="$t('pos.lbl_note_ph')" v-model="store.note"></textarea>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { usePos } from '../../store/pos'
import { posRequest } from '../usePosRequest'
import { POS_EMPLOYEE_LIST } from '../constants/pos'
import { money } from '../money'

const store = usePos()

const currencySymbol = (typeof window !== 'undefined' && window.defaultCurrencySymbol) || '$'

const lineCount = computed(() => store.services.length + store.products.length)

const lineTotal = (line) => (Number(line.unit_price) || 0) * (Number(line.qty) || 0) - (Number(line.line_discount) || 0)

const employeeName = (employee) => {
  const name = `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
  return name || employee.email || `#${employee.id}`
}

/**
 * Empleados por linea de servicio. Se reutiliza el endpoint que ya usa el flujo
 * de reserva (`api/quick-booking/employee-list`), que filtra por servicio Y
 * sucursal: no se inventa un endpoint nuevo ni se replica la regla en el front.
 */
const loadEmployees = async (line) => {
  if (!store.branchId || !line.service_id) return

  store.setServiceLoading(line.uid, true)

  const res = await posRequest(POS_EMPLOYEE_LIST({ branch_id: store.branchId, service_id: line.service_id }))

  store.setServiceLoading(line.uid, false)

  if (!res.ok) {
    store.setServiceEmployees(line.uid, [])
    return
  }

  const employees = Array.isArray(res.data) ? res.data : []
  store.setServiceEmployees(line.uid, employees)

  // Un solo empleado posible -> se preselecciona (evita un tap inutil en mostrador).
  if (employees.length === 1) {
    store.setServiceEmployee(line.uid, employees[0].id)
  }
}

// Solo se cargan los empleados de las lineas nuevas (las ya pobladas no se tocan,
// para no pisar la seleccion del cajero).
watch(
  () => store.services.map((line) => line.uid).join(','),
  () => {
    store.services.forEach((line) => {
      if (!line.employeesLoaded && !line.loadingEmployees) loadEmployees(line)
    })
  },
  { immediate: true }
)
</script>
